import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2, Loader2, Pencil, BarChart3, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/client';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; border: string }> = {
  available:     { label: 'ადგილზეა',      color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)'  },
  checked_out:   { label: 'გატანილია',     color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  damaged:       { label: 'დაზიანებულია',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)'  },
  lost:          { label: 'დაკარგულია',    color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)' },
  decommissioned:{ label: 'ჩამოსაწერია',  color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.3)' },
};

const MANUAL_STATUSES = ['available', 'checked_out', 'damaged', 'lost', 'decommissioned'];

const STAT_CARDS = [
  { key: 'available',      icon: 'ti-circle-check',    label: 'ადგილზეა'       },
  { key: 'checked_out',    icon: 'ti-arrows-exchange', label: 'გატანილია'      },
  { key: 'damaged',        icon: 'ti-tool',            label: 'დაზიანებულია'   },
  { key: 'lost',           icon: 'ti-map-pin-off',     label: 'დაკარგულია'     },
  { key: 'decommissioned', icon: 'ti-trash',           label: 'ჩამოსაწერია'    },
];

const LIMIT = 50;

// ── Inventory status badge (scan result) ─────────────────────────────────────

const INV_STATUS_MAP = {
  found:   { label: 'სკანირებულია', color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)'  },
  pending: { label: 'შესამოწმებელი', color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.2)' },
};

function InvStatusBadge({ invStatus }: { invStatus: string | null }) {
  if (!invStatus) return <span style={{ color: 'var(--text-dimmed)', fontSize: 11 }}>—</span>;
  const cfg = INV_STATUS_MAP[invStatus as keyof typeof INV_STATUS_MAP] ?? INV_STATUS_MAP.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      color: cfg.color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

// ── Edit copy modal (inventory status only) ───────────────────────────────────

function EditCopyModal({ copy, onClose, onSaved }: { copy: any; onClose: () => void; onSaved: (updated: any) => void }) {
  const [invStatus, setInvStatus] = useState<string>(copy.inv_status ?? 'pending');
  const [saving, setSaving]       = useState(false);
  const hasSession                = copy.inv_status !== null && copy.inv_status !== undefined;

  const save = async () => {
    if (!hasSession) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch(`/inventory/copies/${copy.id}/inv_status`, { inv_status: invStatus });
      toast.success('შენახულია');
      onSaved({ ...copy, inv_status: invStatus });
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.error;
      toast.error(msg === 'no_active_session' ? 'აქტიური სესია არ არის' : 'შეცდომა');
    }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:16,width:'100%',maxWidth:400,boxShadow:'var(--shadow-lg)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid var(--border)' }}>
          <h2 style={{ fontSize:14,fontWeight:600,color:'var(--text-primary)',margin:0 }}>ინვენტარიზაციის სტატუსი</h2>
          <button onClick={onClose} style={{ background:'none',border:'none',color:'var(--text-faint)',cursor:'pointer',padding:4,display:'flex' }}><X size={16}/></button>
        </div>

        <div style={{ padding:'18px 20px 20px' }}>
          {/* book info */}
          <div style={{ marginBottom:18,padding:'10px 12px',background:'var(--bg-hover)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',lineHeight:1.5 }}>
            <div style={{ fontWeight:600 }}>{copy.title}</div>
            {copy.author && <div style={{ color:'var(--text-faint)',marginTop:2 }}>{copy.author}</div>}
            <div style={{ color:'var(--text-dimmed)',fontSize:11,marginTop:2,fontFamily:'monospace' }}>{copy.barcode}</div>
          </div>

          {!hasSession ? (
            <div style={{ padding:'12px',borderRadius:8,background:'rgba(251,191,36,0.08)',border:'1px solid rgba(251,191,36,0.25)',fontSize:12,color:'#fbbf24',marginBottom:16 }}>
              ინვენტარიზაციის სესია არ მიმდინარეობს
            </div>
          ) : (
            <div style={{ display:'flex',gap:8,marginBottom:20 }}>
              {(['found','pending'] as const).map(k => {
                const cfg = INV_STATUS_MAP[k];
                const active = invStatus === k;
                return (
                  <button key={k} onClick={() => setInvStatus(k)} style={{
                    flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                    padding:'10px 14px', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer',
                    background: active ? cfg.bg : 'transparent',
                    border: `2px solid ${active ? cfg.color : 'var(--border)'}`,
                    color: active ? cfg.color : 'var(--text-muted)',
                    transition:'all 0.15s',
                  }}>
                    <span style={{ width:8,height:8,borderRadius:'50%',background:cfg.color,flexShrink:0 }} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
            <button onClick={onClose} style={{ padding:'8px 16px',borderRadius:8,fontSize:12,background:'transparent',border:'1px solid var(--border)',color:'var(--text-muted)',cursor:'pointer' }}>
              გაუქმება
            </button>
            {hasSession && (
              <button onClick={save} disabled={saving} style={{ padding:'8px 20px',borderRadius:8,fontSize:12,fontWeight:600,background:'var(--accent)',border:'none',color:'#fff',cursor:saving?'not-allowed':'pointer',opacity:saving?0.7:1 }}>
                {saving ? '...' : 'შენახვა'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Accounting report modal ───────────────────────────────────────────────────

function AccountingModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['inventory-accounting'],
    queryFn: () => api.get('/inventory/accounting'),
  } as any);
  const rows       = (data as any)?.data?.rows ?? [];
  const grandTotal = (data as any)?.data?.grand_total ?? 0;

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:16,width:'100%',maxWidth:700,maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'var(--shadow-lg)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid var(--border)',flexShrink:0 }}>
          <div>
            <h2 style={{ fontSize:14,fontWeight:600,color:'var(--text-primary)',margin:0 }}>საბუღალტრო ანგარიში</h2>
            <p style={{ fontSize:11,color:'var(--text-faint)',margin:'2px 0 0' }}>სულ: <strong style={{ color:'var(--accent)' }}>{Number(grandTotal).toLocaleString('ka-GE',{minimumFractionDigits:2})} ₾</strong></p>
          </div>
          <button onClick={onClose} style={{ background:'none',border:'none',color:'var(--text-faint)',cursor:'pointer',padding:4,display:'flex' }}><X size={16}/></button>
        </div>

        <div style={{ overflowY:'auto',flex:1 }}>
          {isLoading ? (
            <div style={{ padding:40,textAlign:'center',color:'var(--text-dimmed)' }}>იტვირთება...</div>
          ) : (
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead style={{ position:'sticky',top:0,background:'var(--bg-card)',zIndex:1 }}>
                <tr style={{ background:'var(--bg-hover)' }}>
                  {['სრული ანგარიში','ასლები','სულ ₾','საშ. ₾'].map(h => (
                    <th key={h} style={{ padding:'9px 14px',textAlign:h==='ასლები'||h==='სულ ₾'||h==='საშ. ₾'?'right':'left',fontSize:11,fontWeight:600,color:'var(--text-muted)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any) => (
                  <tr key={r.full_account || '—'} style={{ borderBottom:'1px solid var(--border)' }}
                    onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                    <td style={{ padding:'8px 14px',fontFamily:'monospace',fontSize:12,color:'var(--text-secondary)',fontWeight:500 }}>{r.full_account || '—'}</td>
                    <td style={{ padding:'8px 14px',textAlign:'right',color:'var(--text-muted)' }}>{Number(r.copy_count).toLocaleString()}</td>
                    <td style={{ padding:'8px 14px',textAlign:'right',color:'var(--accent)',fontWeight:600 }}>{Number(r.total_value).toLocaleString('ka-GE',{minimumFractionDigits:2})}</td>
                    <td style={{ padding:'8px 14px',textAlign:'right',color:'var(--text-faint)' }}>{Number(r.avg_price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:'var(--bg-hover)',borderTop:'2px solid var(--border)' }}>
                  <td style={{ padding:'10px 14px',fontWeight:700,color:'var(--text-primary)',fontSize:12 }}>სულ</td>
                  <td style={{ padding:'10px 14px',textAlign:'right',fontWeight:700,color:'var(--text-primary)' }}>
                    {rows.reduce((s: number, r: any) => s + Number(r.copy_count), 0).toLocaleString()}
                  </td>
                  <td style={{ padding:'10px 14px',textAlign:'right',fontWeight:700,color:'var(--accent)',fontSize:13 }}>
                    {Number(grandTotal).toLocaleString('ka-GE',{minimumFractionDigits:2})} ₾
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Scan result popup ─────────────────────────────────────────────────────────

function ScanPopup({ scan, onClose }: { scan: any; onClose: () => void }) {
  const [pct, setPct] = useState(100);
  useEffect(() => {
    const start = Date.now();
    const dur   = 2000;
    const tick  = () => {
      const elapsed = Date.now() - start;
      const rem = Math.max(0, 100 - (elapsed / dur) * 100);
      setPct(rem);
      if (rem > 0) requestAnimationFrame(tick);
      else onClose();
    };
    requestAnimationFrame(tick);
  }, [onClose]);

  const isFound = scan.result === 'found';
  const color   = isFound ? '#34d399' : '#f87171';
  const bg      = isFound ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)';
  const border  = isFound ? 'rgba(52,211,153,0.4)' : 'rgba(248,113,113,0.4)';

  return (
    <div style={{
      position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
      zIndex: 2000, width: 380, maxWidth: 'calc(100vw - 32px)',
      background: 'var(--bg-card)', border: `1px solid ${border}`,
      borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: bg, border: `1px solid ${border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isFound
            ? <CheckCircle2 size={18} style={{ color }} />
            : <AlertTriangle size={18} style={{ color }} />
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {isFound ? 'სკანირებულია' : scan.result === 'duplicate' ? 'უკვე სკანირებული' : 'ვერ მოიძებნა'}
          </div>
          {scan.title && (
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {scan.title}
            </div>
          )}
          {scan.author && (
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {scan.author}
            </div>
          )}
          {scan.barcode && (
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', fontFamily: 'monospace', marginTop: 3 }}>
              {scan.barcode}
            </div>
          )}
        </div>
        {isFound && scan.remaining !== undefined && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#a78bfa', lineHeight: 1 }}>{scan.remaining.toLocaleString()}</div>
            <div style={{ fontSize: 9, color: 'var(--text-faint)', marginTop: 2 }}>დარჩა</div>
          </div>
        )}
      </div>
      {/* countdown bar */}
      <div style={{ height: 3, background: 'var(--border)' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'none' }} />
      </div>
    </div>
  );
}

// ── Session panel ─────────────────────────────────────────────────────────────

function SessionPanel({ onSessionChange, onCopyScanned }: { onSessionChange: () => void; onCopyScanned: (copyId: number) => void }) {
  const [barcodeInput, setBarcodeInput] = useState('');
  const [starting, setStarting]         = useState(false);
  const [scanning, setScanning]         = useState(false);
  const [scanPopup, setScanPopup]       = useState<any>(null);
  const inputRef   = useRef<HTMLInputElement>(null);
  const autoTimer  = useRef<any>(null);
  const qc = useQueryClient();

  const { data: activeData, refetch: refetchActive } = useQuery({
    queryKey: ['inventory-session-active'],
    queryFn: () => api.get('/inventory/sessions/active'),
    refetchInterval: 5000,
  } as any);

  const sessionInfo  = (activeData as any)?.data;
  const session      = sessionInfo?.session;
  const totalCopies  = sessionInfo?.total_copies ?? 0;
  const found        = sessionInfo?.found ?? 0;
  const remaining    = sessionInfo?.remaining ?? 0;

  const startSession = async () => {
    if (!window.confirm('დაიწყოთ ინვენტარიზაცია? ასლების სტატუსი არ შეიცვლება — სკანირება ითვლება სათითაოდ.')) return;
    setStarting(true);
    try {
      const { data } = await api.post('/inventory/sessions');
      toast.success(`ინვენტარიზაცია დაიწყო — ${data.total_copies?.toLocaleString()} ასლი`);
      await refetchActive();
      qc.invalidateQueries({ queryKey: ['inventory-stats'] });
      qc.invalidateQueries({ queryKey: ['inventory-copies'] });
      onSessionChange();
    } catch { toast.error('შეცდომა'); }
    finally { setStarting(false); }
  };

  const finishSession = async () => {
    if (!session) return;
    if (!window.confirm(`დაასრულოთ ინვენტარიზაცია?\nნაპოვნია: ${found}, ვერ სკანირდა: ${remaining}`)) return;
    try {
      const { data } = await api.put(`/inventory/sessions/${session.id}/finish`);
      toast.success(`დასრულდა — ნაპოვნია: ${data.found}, ვერ მოიძებნა: ${data.not_found}`);
      await refetchActive();
      qc.invalidateQueries({ queryKey: ['inventory-stats'] });
      qc.invalidateQueries({ queryKey: ['inventory-copies'] });
      onSessionChange();
    } catch { toast.error('შეცდომა'); }
  };

  const cancelSession = async () => {
    if (!session) return;
    if (!window.confirm('გაუქმდეს ინვენტარიზაციის სესია? ასლების სტატუსი არ შეიცვლება.')) return;
    try {
      await api.put(`/inventory/sessions/${session.id}/cancel`);
      toast.success('ინვენტარიზაცია გაუქმდა');
      await refetchActive();
      qc.invalidateQueries({ queryKey: ['inventory-stats'] });
      qc.invalidateQueries({ queryKey: ['inventory-copies'] });
      onSessionChange();
    } catch { toast.error('შეცდომა'); }
  };

  const doScan = async (bc: string) => {
    if (!bc || !session) return;
    clearTimeout(autoTimer.current);
    setScanning(true);
    try {
      const { data } = await api.post(`/inventory/sessions/${session.id}/scan`, { barcode: bc });
      setBarcodeInput('');
      setScanPopup({ ...data, barcode: bc });
      if (data.result === 'found') {
        onCopyScanned(data.copy_id);
        refetchActive();
      }
    } catch { toast.error('შეცდომა'); }
    finally { setScanning(false); setTimeout(() => inputRef.current?.focus(), 50); }
  };

  const scanBarcode = (e: React.FormEvent) => { e.preventDefault(); doScan(barcodeInput.trim()); };

  const handleBarcodeChange = (val: string) => {
    setBarcodeInput(val);
    clearTimeout(autoTimer.current);
    if (val.trim()) {
      autoTimer.current = setTimeout(() => doScan(val.trim()), 1000);
    }
  };

  // No active session
  if (!session) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderRadius: 12, marginBottom: 20,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>
            ინვენტარიზაციის სესია
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
            სესია არ მიმდინარეობს
          </div>
        </div>
        <button onClick={startSession} disabled={starting} style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '9px 20px', borderRadius: 9,
          background: 'var(--accent)', border: 'none', color: '#fff',
          fontSize: 13, fontWeight: 600, cursor: starting ? 'not-allowed' : 'pointer',
          opacity: starting ? 0.7 : 1, boxShadow: '0 2px 8px var(--accent-glow)',
        }}>
          {starting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <i className="ti ti-clipboard-list" style={{ fontSize: 16 }} />}
          ინვენტარიზაციის დაწყება
        </button>
      </div>
    );
  }

  // Active session
  const pct = totalCopies > 0 ? Math.round((found / totalCopies) * 100) : 0;

  return (
    <div style={{
      padding: '16px 20px', borderRadius: 12, marginBottom: 20,
      background: 'rgba(167,139,250,0.06)',
      border: '1px solid rgba(167,139,250,0.35)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      {/* top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%', background: '#a78bfa',
            boxShadow: '0 0 6px #a78bfa', flexShrink: 0,
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>
              ინვენტარიზაცია მიმდინარეობს
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 1 }}>
              დაიწყო: {new Date(session.started_at).toLocaleString('ka-GE')}
              {session.started_by_name ? ` · ${session.started_by_name}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={cancelSession} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
            background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)',
          }}>
            გაუქმება
          </button>
          <button onClick={finishSession} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)', color: '#34d399',
          }}>
            <CheckCircle2 size={14} /> დასრულება
          </button>
        </div>
      </div>

      {/* progress bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 11 }}>
          <span style={{ color: 'var(--text-faint)' }}>
            ნაპოვნია <strong style={{ color: '#34d399' }}>{found.toLocaleString()}</strong> / {totalCopies.toLocaleString()} ·
            დარჩა <strong style={{ color: '#a78bfa' }}>{remaining.toLocaleString()}</strong>
          </span>
          <span style={{ color: '#a78bfa', fontWeight: 700 }}>{pct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: 'rgba(167,139,250,0.2)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`, borderRadius: 3,
            background: 'linear-gradient(90deg,#a78bfa,#34d399)', transition: 'width 0.4s ease',
          }} />
        </div>
      </div>

      {/* scanner */}
      <form onSubmit={scanBarcode} style={{ display: 'flex', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <i className="ti ti-barcode" style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-dimmed)', fontSize: 16,
          }} />
          <input
            ref={inputRef}
            value={barcodeInput}
            onChange={e => handleBarcodeChange(e.target.value)}
            placeholder="ბარკოდი — სკანირება ან ხელით შეყვანა..."
            autoFocus
            style={{
              width: '100%', padding: '9px 12px 9px 34px',
              background: 'var(--bg-input)', border: '1px solid rgba(167,139,250,0.4)',
              borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)',
              outline: 'none', boxSizing: 'border-box',
            }} />
        </div>
        <button type="submit" disabled={scanning || !barcodeInput.trim()} style={{
          padding: '9px 18px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: '#a78bfa', border: 'none', color: '#fff', cursor: 'pointer',
          opacity: scanning || !barcodeInput.trim() ? 0.6 : 1,
        }}>
          {scanning ? '...' : 'სკანირება'}
        </button>
      </form>

      {scanPopup && <ScanPopup scan={scanPopup} onClose={() => setScanPopup(null)} />}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const [page, setPage]               = useState(1);
  const [q, setQ]                     = useState('');
  const [inputQ, setInputQ]           = useState('');
  const [invStatusFilter, setInvStatus] = useState('');
  const [copies, setCopies]           = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editCopy, setEditCopy]   = useState<any>(null);
  const [showAccounting, setShowAccounting] = useState(false);
  const debounceRef = useRef<any>(null);
  const qc = useQueryClient();

  const handleSearch = (val: string) => {
    setInputQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setQ(val); setPage(1); }, 350);
  };

  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['inventory-stats', refreshKey],
    queryFn: () => api.get('/inventory/stats'),
    staleTime: 5000,
  } as any);
  const stats = (statsData as any)?.data ?? {};

  const { data, isLoading } = useQuery({
    queryKey: ['inventory-copies', q, invStatusFilter, page, refreshKey],
    queryFn: () => api.get('/inventory/copies', { params: { q, inv_status: invStatusFilter, page, limit: LIMIT } }),
    keepPreviousData: true,
  } as any);

  useEffect(() => {
    setCopies((data as any)?.data?.copies ?? []);
  }, [data]);

  const total      = (data as any)?.data?.total ?? 0;
  const totalValue = (data as any)?.data?.total_value ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const handleSaved = useCallback((updated: any) => {
    setCopies(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
    refetchStats();
  }, [refetchStats]);

  const handleCopyScanned = useCallback((copyId: number) => {
    setCopies(prev => prev.map(c => c.id === copyId ? { ...c, inv_status: 'found' } : c));
  }, []);

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui,sans-serif' }}>

      {editCopy && <EditCopyModal copy={editCopy} onClose={() => setEditCopy(null)} onSaved={handleSaved} />}
      {showAccounting && <AccountingModal onClose={() => setShowAccounting(false)} />}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.3px' }}>
            ინვენტარი
          </h1>
          <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '3px 0 0' }}>
            {stats.total ? `${Number(stats.total).toLocaleString()} ასლი` : ''}
          </p>
        </div>
        <button onClick={() => setShowAccounting(true)} style={{
          display:'flex',alignItems:'center',gap:7,padding:'8px 16px',borderRadius:9,
          background:'var(--bg-card)',border:'1px solid var(--border)',
          color:'var(--text-secondary)',fontSize:12,fontWeight:500,cursor:'pointer',
          boxShadow:'var(--shadow-sm)',transition:'all 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text-secondary)'; }}>
          <BarChart3 size={14}/> საბუღ. ანგარიში
        </button>
      </div>

      {/* Session panel */}
      <SessionPanel onSessionChange={() => { setRefreshKey(k => k + 1); }} onCopyScanned={handleCopyScanned} />

      {/* Stat cards — circulation overview (read-only) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 20 }}>
        {STAT_CARDS.map(sc => {
          const cfg   = STATUS_MAP[sc.key];
          const count = stats[sc.key] ?? 0;
          return (
            <div key={sc.key} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              padding: '12px 14px', borderRadius: 12,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, marginBottom: 8,
                background: cfg.bg, border: `1px solid ${cfg.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className={`ti ${sc.icon}`} style={{ fontSize: 14, color: cfg.color }} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
                {Number(count).toLocaleString()}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-faint)', marginTop: 3, fontWeight: 500 }}>
                {sc.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Inventory status filter — only when session is active */}
      {invStatusFilter !== '' && (
        <div style={{ marginBottom: 10 }} />
      )}

      {/* Table card */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 360 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dimmed)' }} />
            <input value={inputQ} onChange={e => handleSearch(e.target.value)} placeholder="სათაური ან ბარკოდი..."
              style={{ width: '100%', padding: '8px 12px 8px 32px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {/* Inventory status filter buttons */}
          {(['found', 'pending'] as const).map(k => {
            const cfg = INV_STATUS_MAP[k];
            const active = invStatusFilter === k;
            return (
              <button key={k} onClick={() => { setInvStatus(active ? '' : k); setPage(1); }} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: active ? cfg.bg : 'transparent',
                border: `1px solid ${active ? cfg.border : 'var(--border)'}`,
                color: active ? cfg.color : 'var(--text-muted)',
                transition: 'all 0.15s',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                {cfg.label}
                {active && <i className="ti ti-x" style={{ fontSize: 10, marginLeft: 2 }} />}
              </button>
            );
          })}
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div style={{ fontSize:12, color:'var(--text-faint)' }}>{total.toLocaleString()} ჩანაწ.</div>
            {totalValue > 0 && <div style={{ fontSize:11, color:'var(--accent)', fontWeight:600 }}>{Number(totalValue).toLocaleString('ka-GE',{minimumFractionDigits:2})} ₾</div>}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg-hover)' }}>
                {['#', 'სახელწოდება', 'ასლის ნომერი', 'საბუღ. ანგარიში', 'ფასი ₾', 'ინვ. სტატუსი', ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 12px', textAlign: i >= 4 && i <= 5 ? 'right' : 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', width: i===0?50:i===2?150:i===3?145:i===4?90:i===5?160:50, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-dimmed)' }}>იტვირთება...</td></tr>}
              {!isLoading && copies.length === 0 && <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 13 }}>ჩანაწერი არ მოიძებნა</td></tr>}
              {copies.map((copy: any, idx: number) => (
                <tr key={copy.id}
                  style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ padding: '9px 12px', color: 'var(--text-dimmed)', fontSize: 11 }}>
                    {(page - 1) * LIMIT + idx + 1}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <div style={{ color: 'var(--text-secondary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{copy.title}</div>
                    {copy.author && <div style={{ color: 'var(--text-faint)', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{copy.author}</div>}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-hover)', padding: '2px 6px', borderRadius: 5 }}>
                      {copy.inv_number || copy.barcode || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                    {copy.full_account || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: copy.price ? 'var(--text-secondary)' : 'var(--text-dimmed)', fontWeight: copy.price ? 600 : 400 }}>
                    {copy.price != null ? Number(copy.price).toFixed(2) : '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                    <InvStatusBadge invStatus={copy.inv_status} />
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'center' }}>
                    <button onClick={() => setEditCopy(copy)} title="რედაქტირება" style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 28, height: 28, borderRadius: 7, border: '1px solid var(--border)',
                      background: 'transparent', cursor: 'pointer', color: 'var(--text-dimmed)',
                      transition: 'all 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)'; e.currentTarget.style.background='var(--accent-bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text-dimmed)'; e.currentTarget.style.background='transparent'; }}>
                      <Pencil size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>გვ. {page} / {totalPages || 1}</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => setPage(1)} disabled={page===1} title="პირველი" style={{ padding:'6px 10px',borderRadius:8,fontSize:12,fontWeight:700,cursor:page===1?'not-allowed':'pointer',background:page===1?'var(--bg-card)':'rgba(99,102,241,0.12)',border:`1px solid ${page===1?'var(--border)':'rgba(99,102,241,0.35)'}`,color:page===1?'var(--text-dimmed)':'#818cf8' }}>«</button>
            <button onClick={() => setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ display:'flex',alignItems:'center',gap:4,padding:'6px 12px',borderRadius:8,fontSize:12,fontWeight:500,cursor:page===1?'not-allowed':'pointer',background:page===1?'var(--bg-card)':'rgba(239,68,68,0.1)',border:`1px solid ${page===1?'var(--border)':'rgba(239,68,68,0.35)'}`,color:page===1?'var(--text-dimmed)':'#f87171' }}><ChevronLeft size={13}/> წინა</button>
            <span style={{ padding:'5px 14px',borderRadius:8,fontSize:12,fontWeight:600,background:'var(--accent-bg)',color:'var(--accent)',border:'1px solid rgba(37,99,235,0.25)',minWidth:48,textAlign:'center' }}>{page}</span>
            <button onClick={() => setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} style={{ display:'flex',alignItems:'center',gap:4,padding:'6px 12px',borderRadius:8,fontSize:12,fontWeight:500,cursor:page>=totalPages?'not-allowed':'pointer',background:page>=totalPages?'var(--bg-card)':'rgba(37,99,235,0.1)',border:`1px solid ${page>=totalPages?'var(--border)':'rgba(37,99,235,0.35)'}`,color:page>=totalPages?'var(--text-dimmed)':'#60a5fa' }}>შემდეგი <ChevronRight size={13}/></button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
    </div>
  );
}
